import { DirectionalIcon, SkeletonList, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowUp20Regular,
  ChevronRight20Regular,
  Folder20Regular,
  FolderOpen20Regular,
} from '@fluentui/react-icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { useFolderChildren, useRefreshFolders, useUpdateFolder } from './api';
import {
  canWrite,
  folderErrorMessage,
  isStaleVersion,
  isStepUpRequired,
  type Folder,
} from './model';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"intro":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42"},"where":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Eh141a":"fni485r","Bhrd7zp":"fl43uef"},"list":{"dclx09":"ftrb29c","Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","B1hrpyx":"f1jat206","mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","mlrwpw":"f94mpck","Bmxbyg5":"f5zp4f"},"row":{"Bns2gvi":"f1c209jx","Brf1p80":"fbhxue7","fsow6f":"fpgzoln"},"name":{"Bh6795r":"fqerorx","Btd35i7":"fjksvth"},"muted":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"f11d4kpn"}});

export interface MoveDialogProps {
  spaceId: string;
  /** The folder to move. */
  folder: Folder;
  /** The caller manages the project: a folder may be placed at the root. */
  canManageRoot: boolean;
  onClose: () => void;
  /** After the move: the tab keeps the moved folder open. */
  onMoved?: (folder: Folder) => void;
}

/**
 * Picks the destination of a folder by browsing the tree level by level (a keyboard-friendly
 * alternative to drag and drop), then moves it with `If-Match`. The folder itself is not offered,
 * so a move into its own subtree cannot be picked (the API refuses it as well).
 */
export function MoveDialog({ spaceId, folder, canManageRoot, onClose, onMoved }: MoveDialogProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const update = useUpdateFolder(spaceId);
  const refresh = useRefreshFolders(spaceId);

  // The folders opened from the root down to the browsed location (empty: the root).
  const [path, setPath] = useState<Folder[]>([]);
  const [failure, setFailure] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const here = path.at(-1) ?? null;
  const children = useFolderChildren(spaceId, here?.id ?? null);
  const choices = (children.data?.items ?? []).filter((candidate) => candidate.id !== folder.id);

  const blocked: string | null =
    (here?.id ?? null) === folder.parentId
      ? t('move.alreadyHere')
      : here === null
        ? canManageRoot
          ? null
          : t('move.rootNeedsManager')
        : canWrite(here)
          ? null
          : t('move.noWrite');

  const move = async () => {
    if (pending || blocked) return;
    setPending(true);
    setFailure(null);
    try {
      const moved = await update.mutateAsync({
        folderId: folder.id,
        version: folder.version,
        data: { parentId: here?.id ?? null },
      });
      notify({ intent: 'success', message: t('move.done', { name: isolate(folder.name) }) });
      await refresh();
      onMoved?.(moved);
      onClose();
    } catch (error) {
      if (isStepUpRequired(error)) return;
      if (isStaleVersion(error)) {
        notify({ intent: 'warning', message: t('errors.stale') });
        await refresh();
        onClose();
      } else {
        setFailure(folderErrorMessage(t, error));
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !pending) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('move.title', { name: isolate(folder.name) })}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <p className={styles.intro}>{t('move.intro')}</p>
              <div className={styles.where}>
                <FolderOpen20Regular aria-hidden="true" />
                <span>
                  {t('move.location', {
                    name: isolate(here ? here.name : t('move.root')),
                  })}
                </span>
                {here ? (
                  <Button
                    size="small"
                    appearance="subtle"
                    icon={<ArrowUp20Regular />}
                    onClick={() => {
                      setPath((current) => current.slice(0, -1));
                      setFailure(null);
                    }}
                  >
                    {t('move.up')}
                  </Button>
                ) : null}
              </div>
              {children.isError ? (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>{t('tree.loadFailed')}</MessageBarBody>
                  <MessageBarActions>
                    <Button
                      onClick={() => {
                        void children.refetch();
                      }}
                    >
                      {t('common:actions.retry')}
                    </Button>
                  </MessageBarActions>
                </MessageBar>
              ) : !children.data ? (
                <SkeletonList count={3} />
              ) : choices.length > 0 ? (
                <ul className={styles.list} aria-label={t('move.listLabel')}>
                  {choices.map((choice) => (
                    <li key={choice.id}>
                      <Button
                        appearance="subtle"
                        className={styles.row}
                        icon={<Folder20Regular />}
                        iconPosition="before"
                        aria-label={t('move.open', { name: isolate(choice.name) })}
                        onClick={() => {
                          setPath((current) => [...current, choice]);
                          setFailure(null);
                        }}
                      >
                        <bdi className={styles.name}>{choice.name}</bdi>
                        <DirectionalIcon icon={ChevronRight20Regular} aria-hidden="true" />
                      </Button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className={styles.muted}>{t('move.noSubfolders')}</p>
              )}
              {blocked ? <p className={styles.muted}>{blocked}</p> : null}
              {failure ? (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>{failure}</MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabledFocusable={pending || blocked !== null}
              onClick={() => {
                void move();
              }}
            >
              {pending ? t('move.moving') : t('move.here')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/move-dialog.griffel.css";